import React, { Suspense, lazy } from "react";

import './css/termalcss.css';
import './css/styles.css';
import { BrowserRouter as Router, Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { NotificationContainer } from 'react-notifications';
import 'react-notifications/lib/notifications.css';
import '@fortawesome/fontawesome-free/css/all.min.css';

import SignIn from './components/authentication/SignIn';
import LocalActivation from './components/authentication/LocalActivation';
import ProtectedRoute from './components/utils/ProtectedRoute';
import ConsoleRoute from './components/utils/ConsoleRoute';
import { UserProvider } from './UserContext';

// ⬇️ NEW: live permissions context
import { PermissionsProvider } from './security/PermissionsContext';

import { DemoModeProvider } from "./components/demo/DemoModeContext";
import { ThemeProvider } from "./components/theme/ThemeContext";
import { deploymentModes, getDeploymentMode, isLocalApplianceOrigin, isLocalEditionBuild } from "./services/deploymentConfig";

const ResetPassword = lazy(() => import('./components/authentication/ResetPassword'));
const SetNewPassword = lazy(() => import('./components/authentication/SetNewPassword'));
const FirstTimePasswordUpdate = lazy(() => import('./components/authentication/FirstTimePasswordUpdate'));
const LandingPage = lazy(() => import('./components/utils/LandingPage'));
const ClearHealthLanding = lazy(() => import('./pages/ClearHealthLanding'));
const NewPatientFormSelf = lazy(() => import("./components/patient/NewPatientFormSelf"));
const SelfServiceLanding = lazy(() => import("./SelfServiceLanding"));
const SelfAppointmentForm = lazy(() => import("./components/appointment/SelfAppointmentForm"));
const OperationsConsole = lazy(() => import("./components/operations/OperationsConsole"));
const DeveloperEnvironment = lazy(() => import("./components/support/DeveloperEnvironment"));

const PublicLanding = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const mode = getDeploymentMode();

  if (params.get("mode") === "resetPassword" && params.get("oobCode")) {
    return <Redirect to={`/set-new-password?${params.toString()}`} />;
  }

  if (isLocalApplianceOrigin()) {
    return <Redirect to="/login" />;
  }

  if (isLocalEditionBuild() || mode === deploymentModes.OFFLINE || mode === deploymentModes.HYBRID) {
    return <Redirect to="/activate-local" />;
  }

  return <ClearHealthLanding />;
};

function App() {
  return (
    <ThemeProvider>
      <Router>
        <UserProvider>
          {/* Wrap routes with PermissionsProvider so `can()` works everywhere */}
          <PermissionsProvider>
            <DemoModeProvider>
              <Suspense fallback={null}>
                <Switch>
                  {/* Public Routes */}
                  <Route exact path="/activate-local" component={LocalActivation} />
                  <Route exact path="/login" component={SignIn} />
                  <Route exact path="/" component={PublicLanding} />
                  <Route exact path="/clearhealth" component={PublicLanding} />
                  <Route exact path="/reset-password" component={ResetPassword} />
                  <Route exact path="/set-new-password" component={SetNewPassword} />
                  <Route exact path="/__/auth/action" component={SetNewPassword} />
                  <Route exact path="/password-update" component={FirstTimePasswordUpdate} />
                  <Route exact path="/self-registration" component={NewPatientFormSelf} />
                  <Route exact path="/self-appointment" component={SelfAppointmentForm} />
                  <Route exact path="/support/developer-environment" component={DeveloperEnvironment} />

                  {/* Self-service by hospital */}
                  <Route exact path="/h/:hospitalSlug/self-service" component={SelfServiceLanding} />
                  <ConsoleRoute exact path="/operations-console" component={OperationsConsole} />

                  {/* Catch-all Protected Routes (no module check here, just auth) */}
                  <ProtectedRoute path="/" component={LandingPage} />
                </Switch>
              </Suspense>
              <NotificationContainer />
            </DemoModeProvider>
          </PermissionsProvider>
        </UserProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
